import React, { useState } from "react";
import { App, Typography } from "antd";
import { StopOutlined } from "@ant-design/icons";

import { confirmAction } from "@components/Page";
import { TableRowActions } from "@components/Table";
import { useAppContext } from "@components/AppContext";
import { type AppContext } from "@components/AppContext/types";
import { revokeMyPersonalAccessTokenOperation } from "@api/services/identity";
import { type PersonalAccessToken } from "@api/types";

const LABEL_REVOKE = "Revoke";
const LABEL_CONFIRM_TITLE = "Revoke this personal access token?";
const LABEL_CONFIRM_CONTENT = "Any client using this token stops working immediately. This cannot be undone.";
const LABEL_SUCCESS_MESSAGE = "Personal access token $ is revoked";

const STATUS_ACTIVE = "ACTIVE";

type RevokeOutput = {
  data: PersonalAccessToken;
};

type Props = {
  personalAccessToken: PersonalAccessToken;
  onRevoked: (revokedToken: PersonalAccessToken) => void;
};

const PersonalAccessTokenRowActions = ({
  personalAccessToken,
  onRevoked,
}: Props): JSX.Element => {
  const { modal } = App.useApp();
  const {
    authenticatedRequest,
    showSuccessMessage,
  } = useAppContext() as AppContext;

  const [ isLoading, setIsLoading ] = useState(false);

  const { id, name, status } = personalAccessToken;

  const revokeToken = () => {
    setIsLoading(true);

    return authenticatedRequest<RevokeOutput>(
      revokeMyPersonalAccessTokenOperation,
      { id },
    )
      .then(({ data }) => {
        onRevoked(data);
        showSuccessMessage(LABEL_SUCCESS_MESSAGE.replace("$", `"${name}"`));
      })
      .finally(() => setIsLoading(false));
  };

  const openRevokeConfirm = () =>
    confirmAction(
      modal,
      LABEL_REVOKE,
      LABEL_CONFIRM_TITLE,
      LABEL_CONFIRM_CONTENT,
      revokeToken,
      "danger",
    );

  const isRevoked = status !== STATUS_ACTIVE;

  // NOTE: a revoked token has no action left, TableRowActions renders
  //       nothing when the menu is empty.
  const items = isRevoked
    ? []
    : [
      {
        key: "revoke",
        icon: <StopOutlined />,
        danger: true,
        label: (
          <Typography.Link onClick={openRevokeConfirm}>
            {LABEL_REVOKE}
          </Typography.Link>
        ),
      },
    ];

  return (
    <TableRowActions items={items} isLoading={isLoading} />
  );
};

export default PersonalAccessTokenRowActions;
